// src/components/helpcenter/knowledgeBase.js
// Local knowledge base for eHandshake AI Help Assistant.
// Each entry has keywords for matching, a canonical question, and a formatted answer.
// Searched locally first to avoid unnecessary AI API calls.

const KNOWLEDGE_BASE = [

  // ── PROFILE ──────────────────────────────────────────────────────────
  {
    id: 'profile-change',
    category: 'profile',
    keywords: ['change profile', 'edit profile', 'update profile', 'modify profile', 'profile photo', 'profile picture', 'business description', 'about us', 'profile assistant'],
    question: 'How do I change my profile?',
    answer: `Changing your profile is easy — and we encourage you to do it often!

Your profile, company profile, or team profile can be shared to all of the social platforms we support, and by email.

You want your profile to change often. Adding reviews, updating your business description, or changing your profile photo signals a dated change and your profile becomes shareable. If no changes have been made, it becomes available for resharing every 30 days.

**To edit your profile:**
Click the **Profile Assistant** button on the docking station (the toolbar on the right side of the screen). It opens a guided panel where you can update your photo, business info, description, and more.`
  },

  {
    id: 'profile-photo',
    category: 'profile',
    keywords: ['profile photo', 'profile picture', 'change photo', 'upload photo', 'avatar', 'headshot'],
    question: 'How do I change my profile photo?',
    answer: `To change your profile photo:

1. Click the **Profile Assistant** button on the docking station (right side of the screen)
2. Click on your current photo or the photo placeholder
3. Upload a new image — it will be cropped to fit

A professional profile photo makes a big difference when your profile gets shared to social media. People connect with faces, not logos.`
  },

  {
    id: 'company-logo',
    category: 'profile',
    keywords: ['company logo', 'business logo', 'upload logo', 'change logo'],
    question: 'How do I change my company logo?',
    answer: `Your company logo appears on your profile and in shared content. To update it:

1. Open the **Profile Assistant** from the docking station
2. Navigate to the company/business section
3. Click on the logo area to upload a new image

Your logo is displayed alongside your profile when teammates share it to social media.`
  },

  // ── CUSTOMER FOCUS ───────────────────────────────────────────────────
  {
    id: 'customer-focus',
    category: 'customer-focus',
    keywords: ['customer focus', 'b2b', 'b2c', 'b2bc', 'business to business', 'business to consumer', 'focus type', 'what is customer focus'],
    question: 'What is customer focus?',
    answer: `Every business on eHandshake has a **customer focus** — it describes who your customers are. You set this when you create your profile.

There are three types:
• **B2B** (Business to Business) — You sell to other businesses. Examples: accountant serving corporate clients, commercial cleaning, wholesale distributor.
• **B2C** (Business to Consumer) — You sell directly to consumers. Examples: plumber, roofer, hair salon.
• **B2BC** (Business to Business & Consumer) — You do both. Example: real estate agent working with homeowners and commercial properties.

**Why it matters:** Customer focus determines who you should team up with. A B2B accountant sharing a B2C plumber's post to their corporate LinkedIn audience won't get the best results. But a B2C plumber teaming up with a B2C electrician and painter? Their audiences overlap perfectly — homeowners who need services.`
  },

  {
    id: 'customer-focus-matching',
    category: 'customer-focus',
    keywords: ['team matching', 'focus matching', 'right team', 'team fit', 'b2b team', 'b2c team'],
    question: 'How does customer focus affect team matching?',
    answer: `**B2B** users should team with other **B2B** users. **B2C** with **B2C**. Their audiences are fundamentally different, and cross-posting dilutes the message.

**B2BC** users have more flexibility — since they serve both businesses and consumers, they can join a B2B or B2C team and add value either way. It's up to the team captain to decide if they want that flexibility.

When you browse teams, eHandshake shows each team's customer focus so you can find the right fit.`
  },

  // ── TEAMS ────────────────────────────────────────────────────────────
  {
    id: 'what-is-team-definition',
    category: 'teams',
    keywords: ['what is a team', 'what are teams', 'define team', 'team definition', 'team explained', 'profession exclusive'],
    question: 'What is a team?',
    answer: `A **team** is a group of local businesses that agree to work together to help gain exposure for their brand.

Each team is **profession exclusive** — only one plumber, one painter, one real estate agent, one electrician can be on a team together. No competition within teams.

**For example:** A team might have a plumber, an electrician, a painter, a roofer, a landscaper, and an attorney. They all serve homeowners (B2C) but none of them compete with each other. When one teammate shares your post, their audience sees a trusted recommendation — not a competitor's ad.

This exclusivity is what makes teams powerful. Your teammates have every reason to promote you because you're not taking business away from them — you're adding value to their network.

Team captains control who joins, so the right mix of professions is maintained.`
  },

  {
    id: 'team-size',
    category: 'teams',
    keywords: ['how many teammates', 'how many on a team', 'team size', 'team members', 'how big', 'team limit', 'maximum team', 'how many people'],
    question: 'How many teammates can be on a team?',
    answer: `There's no fixed limit — it depends on how many different professions are in your area.

Since each team is **profession exclusive** (only one of each profession), the size of your team is determined by how many non-competing businesses you can recruit. We've seen well over **100 different professions** form teams on eHandshake.

It really depends on where you live and do business. A large city will have many professions available — plumbers, electricians, painters, roofers, attorneys, accountants, caterers, photographers, and dozens more. A smaller town may have fewer options, but even a team of 8-10 diverse professionals can dramatically multiply your reach.

**The bigger your team, the more channels your content reaches.** Every teammate adds their own social media audience to the network.`
  },

  {
    id: 'what-does-team-do',
    category: 'teams',
    keywords: ['team benefits', 'why team', 'team do for me', 'team advantage', 'team value', 'team help'],
    question: 'What does a team do for me?',
    answer: `A team multiplies your reach across social media channels you don't have access to on your own.

**The problem with one channel:** When you post to your Facebook page, the same 200 followers see it. That's drip marketing — consistently showing up. But your audience only grows as fast as your followers do.

**How teams work:** When you join a team, your teammates' creations appear in your Sharing Center — and yours appear in theirs. When a teammate likes your creation, they share it to their own social channels. Their audience sees it. Then every teammate gets a daily digest showing what was shared. They can reshare it on their own channels.

**The math:** You post to Facebook → 200 people see it. A teammate shares to LinkedIn → 600 more. Another to Instagram → 350 more. A third to their Facebook page → 275 more. From one creation: **1,425 people** across 4 channels instead of 200 on one.

**We call it Drip Networking** — the next level of drip marketing. Your content reaches audiences you've never had access to, on platforms you may not even be on.`
  },

  {
    id: 'create-team',
    category: 'teams',
    keywords: ['create team', 'build team', 'start team', 'new team', 'form team', 'team captain', 'lead team', 'make a team'],
    question: 'How do I create a team?',
    answer: `To create your own team:

1. Go to the **Team Info** page
2. Click the **Build Your Team** button
3. **Name your team** — the name must be unique across the entire eHandshake platform, regardless of location
4. Click **Activate** to create your team

That's it — your team is live. You're now the team captain. From here, you can browse nearby candidates on the Team Info page, view their profiles, chat with them, and invite them to join your team.`,
    action: { type: 'navigate', route: '/teams', event: 'team:openRegister', label: 'Build Your Team', snapToSidebar: true },
  },

  {
    id: 'join-team',
    category: 'teams',
    keywords: ['join team', 'find team', 'browse teams', 'local teams', 'team list'],
    negativeKeywords: ['leave', 'quit', 'exit', 'resign', 'drop'],
    question: 'How do I join a team?',
    answer: `To join an existing team:

1. Go to the **Team Info** page
2. Switch to the **Local Teams** view to see teams in your area
3. Browse team profiles to find a match for your customer focus
4. **Start a chat with the team captain** to introduce yourself
5. The captain invites you — accept and you're in

Once you're on a team, your creations appear in their Sharing Center and theirs in yours.`,
    action: { type: 'navigate', route: '/teams', event: 'team:switchLayer', eventDetail: { layer: 'local' }, label: 'Browse Teams' },
  },

  {
    id: 'leave-team',
    category: 'teams',
    keywords: ['leave team', 'quit team', 'exit team', 'remove from team', 'leave a team', 'drop team', 'resign team', 'stop being on a team'],
    negativeKeywords: ['create', 'build', 'start', 'form'],
    question: 'How do I leave a team?',
    answer: `To leave a team:

1. Open the **Help Center** (click the help icon on the docking station)
2. Click **Team Management** in the left sidebar
3. You'll see your teams listed with the option to **Leave Team**
4. Click it and confirm — you'll be removed from that team

Once you leave, your creations are removed from that team's Sharing Center and theirs are removed from yours. You can join another team at any time.

**Note:** If you're the team captain, you'll need to either transfer captaincy to another member or dissolve the team before leaving.`
  },

  {
    id: 'team-captain',
    category: 'teams',
    keywords: ['team captain', 'captain role', 'captain responsibilities', 'team leader'],
    question: 'What does a team captain do?',
    answer: `The team captain is the person who created the team. They:

• **Invite and manage members** — decide who joins the team
• **Set the team's customer focus** — B2B, B2C, or B2BC
• **Monitor team activity** — see who's sharing and how active the team is
• **Remove members** if needed

Any team member can share creations, but the captain controls team membership and settings.`
  },

  // ── SHARING CENTER & DESIGN CENTER ───────────────────────────────────
  {
    id: 'what-is-sharing-center',
    category: 'sharing',
    keywords: ['sharing center', 'share post', 'share creation', 'social media', 'post to facebook', 'post to linkedin', 'post to twitter', 'post to instagram', 'share to social'],
    question: 'What is the Sharing Center?',
    answer: `The **Sharing Center** is where you share creations to social media platforms. It shows:

• **Your creations** — posts you've made in the Design Center
• **Teammate creations** — posts your teammates have made (if you're on a team)

For each creation, you can share it to **Facebook, LinkedIn, X (Twitter), Instagram**, or **TikTok** — one platform at a time. Once a creation has been shared to a platform, it's marked as done for that platform (first come, first served among teammates).

You write a personal message, preview how it will look on the platform, and hit Post. eHandshake handles the rest — including generating a rich link preview card with your headline and image.`
  },

  {
    id: 'what-is-design-center',
    category: 'design',
    keywords: ['design center', 'create post', 'create creation', 'design post', 'content creation', 'make a post'],
    question: 'What is the Design Center?',
    answer: `The **Design Center** is where you create content — called "creations" — that can be shared to social media through the Sharing Center.

In the Design Center, you can:
• **Upload images** or choose from stock photos
• **Add headlines** with customizable text overlay
• **Write captions** tailored for each social platform
• **Preview** how your creation will look on Facebook, LinkedIn, X, Instagram, and more
• **Generate a landing page** that gets shared as a rich link preview on social media

Once your creation is ready, it appears in the Sharing Center for you and your teammates to share.`
  },

  {
    id: 'landing-page',
    category: 'design',
    keywords: ['landing page', 'og image', 'link preview', 'preview card', 'social preview', 'rich link'],
    question: 'What is a landing page?',
    answer: `When you create a post in the Design Center, eHandshake can generate a **landing page** — a public web page with your content, headline, image, and a call-to-action.

When this landing page URL is shared to social media, platforms like Facebook and LinkedIn automatically generate a **rich link preview card** showing your headline overlaid on your image. This looks much more professional than a plain text post.

The landing page also includes SEO-friendly metadata, making it discoverable through search engines.`
  },

  // ── CHAT / MESSAGING ─────────────────────────────────────────────────
  {
    id: 'start-chat',
    category: 'chat',
    keywords: ['start chat', 'invite chat', 'message someone', 'send message', 'chat center', 'new conversation', 'find people', 'discover people'],
    question: 'How do I invite someone to chat?',
    answer: `There are three ways to start a conversation:

**1. From the Chat Center:**
Open your Chat Center (click the chat icon on the docking station) and you'll see **Discover People Nearby** in the right panel. Browse professionals within 100 miles, sorted by distance. Click anyone to view their profile, then start a chat.

**2. From the Team page:**
On the Team Info page, click any user pin on the map or any name in the candidate list. Their profile card opens with a **chat button** — click it to start a conversation directly.

**3. Someone invites you:**
When another professional starts a chat with you, it appears automatically in your Chat Center with a notification.

**Tip:** Chatting is the first step to building a team. Get to know someone before inviting them.`
  },

  {
    id: 'chat-center',
    category: 'chat',
    keywords: ['chat center', 'messages', 'conversations', 'chat list', 'pending chat', 'chat request'],
    question: 'How does the Chat Center work?',
    answer: `The **Chat Center** shows all your conversations — both direct messages with individuals and team group chats.

• **Left panel:** Your conversation list, sorted by most recent activity
• **Right panel:** The active conversation, or the Discover People Nearby search when no conversation is selected

When someone sends you a chat request, it appears as a **pending chat** in your list. You can accept or decline.

Messages are delivered in real-time. You'll see typing indicators when someone is writing, and read receipts when they've seen your message.`
  },

  // ── REVIEWS ──────────────────────────────────────────────────────────
  {
    id: 'review-requests',
    category: 'reviews',
    keywords: ['review request', 'send review', 'customer review', 'get reviews', 'review email', 'request review', 'survey'],
    question: 'How do I send review requests?',
    answer: `Reviews build trust. Here's how to collect them:

1. **Add your customer** — go to the Customers page and enter their name, email, and zip code (or bulk upload a CSV)
2. **Send a review request** — click the envelope icon on their customer card. A pre-written email is ready to customize and send.
3. **They receive a secure link** — your customer gets an email with a personal link to leave a review. No account needed. The link is valid for 90 days.
4. **The review appears on your profile** — visible to anyone who views it, including people who find you through a teammate's social post.

**Track your progress** on the Customers page dashboard: Requests Sent, Completed, and Replied To.

**Pro tip:** Export your existing customer list from your CRM as a CSV and upload it to add everyone at once.`,
    action: { type: 'navigate', route: '/customers', label: 'Go to Customers' },
  },

  {
    id: 'google-reviews',
    category: 'reviews',
    keywords: ['google reviews', 'google business', 'import reviews', 'google places'],
    question: 'Can I import my Google reviews?',
    answer: `Yes! eHandshake can import your **Google Business reviews** alongside your eHandshake reviews. They appear on your profile with a Google attribution badge so visitors can see all your reviews in one place.

Your Customers page dashboard shows both eHandshake reviews and Google reviews separately so you can track each source.`
  },

  // ── SUBSCRIPTION / BILLING ───────────────────────────────────────────
  {
    id: 'subscription',
    category: 'subscription',
    keywords: ['subscription', 'billing', 'plan', 'pricing', 'payment', 'upgrade', 'downgrade', 'cancel', 'cost', 'price', 'free trial'],
    question: 'How do I manage my subscription?',
    answer: `To manage your subscription, open the **Help Center** and click **Subscription** in the left sidebar. From there you can view your current plan, upgrade, downgrade, or update your payment method.

If you have questions about pricing, visit the **Pricing** page (link in the top navigation bar).`
  },

  // ── NOTIFICATIONS ────────────────────────────────────────────────────
  {
    id: 'notifications',
    category: 'notifications',
    keywords: ['notifications', 'email notifications', 'alerts', 'digest', 'daily digest', 'notification settings'],
    question: 'How do I change my notifications?',
    answer: `eHandshake sends notifications for:

• **Chat messages** — when someone sends you a message or chat request
• **Team invitations** — when a captain invites you to join their team
• **Review completions** — when a customer completes a review you requested
• **Daily digest** — a summary of team sharing activity

To manage your notification preferences, open the **Help Center** and click **Notifications** in the left sidebar.`
  },

  // ── PRIVACY ──────────────────────────────────────────────────────────
  {
    id: 'privacy',
    category: 'privacy',
    keywords: ['privacy', 'privacy settings', 'data', 'visible', 'hidden', 'who can see'],
    question: 'How do I update my privacy settings?',
    answer: `To manage your privacy settings, open the **Help Center** and click **Privacy** in the left sidebar. From there you can control what information is visible on your profile and who can contact you.`
  },

  // ── SOCIAL PLATFORMS ─────────────────────────────────────────────────
  {
    id: 'connect-facebook',
    category: 'social',
    keywords: ['connect facebook', 'facebook page', 'facebook oauth', 'link facebook', 'facebook account'],
    question: 'How do I connect my Facebook page?',
    answer: `To connect your Facebook page:

1. Go to the **Sharing Center**
2. Select a creation and click to share it to Facebook
3. If not connected, you'll be prompted to **log in with Facebook** and authorize eHandshake
4. Select which Facebook Page you want to post to
5. Once connected, all future posts go to that page automatically

You can disconnect or switch pages at any time from the post modal.`
  },

  {
    id: 'connect-linkedin',
    category: 'social',
    keywords: ['connect linkedin', 'linkedin page', 'linkedin personal', 'linkedin company', 'link linkedin'],
    question: 'How do I connect LinkedIn?',
    answer: `eHandshake supports both **personal LinkedIn profiles** and **company pages**.

1. Go to the **Sharing Center** and select a creation to share to LinkedIn
2. Choose **Personal Profile** or **Company Pages**
3. Authorize eHandshake through LinkedIn's OAuth flow
4. Once connected, you can post to either your personal feed or your company page

You can connect both personal and company at the same time and choose which one to post to each time.`
  },

  {
    id: 'social-platforms',
    category: 'social',
    keywords: ['social media', 'platforms', 'which platforms', 'supported platforms', 'facebook', 'linkedin', 'twitter', 'instagram', 'tiktok', 'x'],
    question: 'Which social media platforms does eHandshake support?',
    answer: `eHandshake currently supports sharing to:

• **Facebook** — Post to your Facebook Page with rich link preview cards
• **LinkedIn** — Post to your personal profile or company page
• **X (Twitter)** — Post with link previews
• **Instagram** — Coming soon (image posts with caption)
• **TikTok** — Coming soon

Each platform shows a live preview of how your post will look before you share it.`
  },

  // ── DOCKING STATION ──────────────────────────────────────────────────
  {
    id: 'docking-station',
    category: 'navigation',
    keywords: ['docking station', 'toolbar', 'sidebar', 'right panel', 'buttons', 'navigation'],
    question: 'What is the docking station?',
    answer: `The **docking station** is the vertical toolbar on the right side of your screen. It gives you quick access to:

• **Profile Assistant** — edit your profile, photo, business info
• **Chat Center** — view and send messages
• **Help Center** — this panel! Get help with any feature
• **Notifications** — see alerts and activity

Click any icon to open that tool. The docking station is always available no matter what page you're on.`
  },

  // ── SERVICE AREA ─────────────────────────────────────────────────────
  {
    id: 'service-area',
    category: 'profile',
    keywords: ['service area', 'radius', 'coverage', 'miles', 'geographic', 'location', 'map'],
    question: 'What is my service area?',
    answer: `Your **service area** defines the geographic radius around your business where you serve customers. It's set during profile setup and can be updated anytime.

Service area matters for:
• **Team matching** — you'll see candidates within your service area on the Team Info page
• **Discover People** — the Chat Center shows professionals near you based on service area overlap
• **Candidate analysis** — when viewing a potential teammate, eHandshake shows how much your service areas overlap on a map

The more overlap you have with a teammate, the more relevant their audience is to your business.`
  },

  // ── CANDIDATE ANALYSIS ───────────────────────────────────────────────
  {
    id: 'candidate-analysis',
    category: 'teams',
    keywords: ['candidate', 'analyze', 'analysis', 'compare', 'user information', 'profile check', 'service area overlap'],
    question: 'How do I analyze a potential teammate?',
    answer: `When you click on a user from the Team Info page or the Chat Center's Discover People panel, their **profile card** opens with detailed analysis tabs:

• **Customer Focus** — shows if your focus types match (B2B, B2C, B2BC)
• **Service Area** — displays a map showing overlap percentage between your coverage areas
• **Reviews** — their customer reviews and ratings
• **Profile Check** — verifies their profile completeness (photo, logo, address, about us)

These tools help you make informed decisions about who to team with. The better the match, the more effective your Drip Networking will be.`
  },

  // ── GENERAL / GETTING STARTED ────────────────────────────────────────
  {
    id: 'getting-started',
    category: 'general',
    keywords: ['get started', 'getting started', 'new user', 'first steps', 'what do i do', 'where do i start', 'beginner', 'help'],
    question: 'How do I get started with eHandshake?',
    answer: `Welcome to eHandshake! Here's the recommended path to get up and running:

1. **Complete your profile** — add a professional photo, business description, and company logo. Click the Profile Assistant on the docking station.
2. **Add your customers** — go to the Customers page and add your customer list (or upload a CSV). Send them review requests to start building social proof.
3. **Explore the Design Center** — create your first post with an image, headline, and captions.
4. **Connect your social accounts** — link your Facebook Page, LinkedIn, and X accounts so you're ready to share.
5. **Find a team** — browse the Team Info page for professionals in your area with matching customer focus. Start chatting, then team up.
6. **Start sharing** — use the Sharing Center to post creations to your social channels. When teammates do the same, your reach multiplies.

That's Drip Networking in action!`
  },

  {
    id: 'what-is-ehandshake',
    category: 'general',
    keywords: ['what is ehandshake', 'about ehandshake', 'how does ehandshake work', 'ehandshake explained', 'platform overview'],
    question: 'What is eHandshake?',
    answer: `**eHandshake** is a professional networking and social marketing platform that helps businesses grow through collaborative sharing.

The core concept: Instead of marketing alone on one social channel, you team up with other professionals in your area. You share each other's content across your respective social media channels — reaching audiences you'd never reach on your own.

**Key features:**
• **Design Center** — create professional social media content
• **Sharing Center** — share creations to Facebook, LinkedIn, X, Instagram, and more
• **Teams** — collaborate with other professionals to multiply your social reach
• **Chat Center** — message potential teammates and build relationships
• **Review Requests** — collect customer reviews that appear on your profile
• **Landing Pages** — auto-generated web pages with rich social media preview cards

It's called **Drip Networking** — the evolution of drip marketing, powered by teamwork.`
  },

  {
    id: 'drip-networking',
    category: 'general',
    keywords: ['drip networking', 'drip marketing', 'what is drip', 'networking concept', 'marketing concept'],
    question: 'What is Drip Networking?',
    answer: `**Drip Marketing** is what you're probably already doing — consistently posting to your social media channel so your audience sees you regularly. It works, but you're limited to the same followers seeing the same content.

**Drip Networking** is the next level. When you join a team on eHandshake, your content enters a network of professionals who share it across their social channels. One creation can ripple out across Facebook pages, LinkedIn profiles, Instagram accounts, and X feeds that you have zero access to on your own.

The key difference:
• **Drip Marketing** = You → Your audience (same pool every time)
• **Drip Networking** = You → Your team → Multiple audiences across multiple platforms (exponential reach)

You never know who's scrolling through your teammate's feed and sees YOUR business for the first time.`
  },
];

// ── Local search function ──────────────────────────────────────────────
// Multi-signal scoring: question similarity > exact keyword phrases > token overlap.
// Returns ONLY the single best match if confident, or empty array if no good match.

// Stop words to ignore in token matching
const STOP_WORDS = new Set([
  'how', 'what', 'where', 'when', 'why', 'who', 'which', 'does', 'did', 'can',
  'could', 'would', 'should', 'the', 'and', 'for', 'are', 'but', 'not', 'you',
  'all', 'her', 'was', 'one', 'our', 'out', 'has', 'have', 'had', 'been', 'this',
  'that', 'with', 'they', 'from', 'will', 'more', 'about', 'into', 'than', 'its',
  'also', 'just', 'like', 'than', 'them', 'then', 'some', 'only', 'very', 'most',
]);

// Normalize a string for comparison: lowercase, strip punctuation, remove stop words
function normalizeQuery(str) {
  return str
    .toLowerCase()
    .replace(/[?!.,;:'"()\-]/g, ' ')
    .split(/\s+/)
    .filter(t => t.length > 1 && !STOP_WORDS.has(t));
}

// Detect the PRIMARY focus of a query when multiple actions appear.
// "join a team and then leave" → focus is "leave" (last action / what they actually want to know)
// "create a team" → focus is "create"
function detectQueryFocus(queryLower) {
  const focusPatterns = [
    { focus: 'leave',      patterns: ['leave', 'quit', 'exit', 'resign', 'drop out', 'stop being'] },
    { focus: 'create',     patterns: ['create', 'build', 'make', 'start', 'form'] },
    { focus: 'join',       patterns: ['join', 'sign up for'] },
    { focus: 'change',     patterns: ['change', 'edit', 'update', 'modify'] },
    { focus: 'connect',    patterns: ['connect', 'link', 'authorize'] },
    { focus: 'send',       patterns: ['send', 'request', 'invite'] },
  ];

  // Find the LAST matching focus in the query string (position-based)
  let lastFocus = null;
  let lastPos = -1;

  for (const { focus, patterns } of focusPatterns) {
    for (const p of patterns) {
      const pos = queryLower.lastIndexOf(p);
      if (pos > lastPos) {
        lastPos = pos;
        lastFocus = focus;
      }
    }
  }

  return lastFocus;
}

export function searchKnowledgeBase(query, maxResults = 1, minScore = 2.0) {
  if (!query || !query.trim()) return [];

  const queryLower = query.toLowerCase().trim();
  const queryTokens = normalizeQuery(query);

  if (queryTokens.length === 0) return [];

  // Detect primary focus of the query
  const queryFocus = detectQueryFocus(queryLower);

  const scored = KNOWLEDGE_BASE.map(entry => {
    let score = 0;
    const questionLower = entry.question.toLowerCase();
    const questionTokens = normalizeQuery(entry.question);

    // ── Signal 1: Question similarity (highest weight) ──
    // If the user's query closely matches the canonical question, this is very likely the right entry.
    // Check what % of question tokens appear in the query.
    const questionTokensInQuery = questionTokens.filter(t => queryTokens.includes(t));
    const questionMatchRatio = questionTokensInQuery.length / questionTokens.length;
    if (questionMatchRatio >= 0.75) score += 10; // strong question match
    else if (questionMatchRatio >= 0.5) score += 5;

    // Also check reverse: what % of query tokens appear in the question
    const queryTokensInQuestion = queryTokens.filter(t => questionTokens.includes(t));
    const reverseMatchRatio = queryTokensInQuestion.length / queryTokens.length;
    if (reverseMatchRatio >= 0.75) score += 5;
    else if (reverseMatchRatio >= 0.5) score += 2;

    // ── Signal 2: Exact keyword phrase match (high weight) ──
    // Multi-word keywords like "create team" or "drip networking" are very specific.
    for (const kw of entry.keywords) {
      const kwLower = kw.toLowerCase();
      if (kwLower.includes(' ')) {
        // Multi-word keyword — exact match in query is very strong
        if (queryLower.includes(kwLower)) score += 8;
      } else {
        // Single-word keyword — weaker signal
        if (queryTokens.includes(kwLower)) score += 1;
      }
    }

    // ── Signal 2b: Negative keywords — hard penalize if query contains words that contradict this entry ──
    if (entry.negativeKeywords) {
      for (const nk of entry.negativeKeywords) {
        if (queryLower.includes(nk)) {
          score -= 15; // heavy penalty — this entry is explicitly wrong for this query
          break;
        }
      }
    }

    // ── Signal 3: Action verb alignment ──
    // "create" vs "what is" vs "change" vs "join" — intent matters
    const actionVerbs = {
      create: ['create', 'build', 'make', 'start', 'form', 'set up', 'setup'],
      change: ['change', 'edit', 'update', 'modify', 'fix'],
      join: ['join', 'find', 'browse', 'look for', 'search'],
      explain: ['what is', 'what does', 'what are', 'explain', 'tell me about', 'describe'],
      howto: ['how do', 'how to', 'how can', 'steps to', 'walk me through'],
      connect: ['connect', 'link', 'authorize', 'log in', 'sign in', 'oauth'],
      send: ['send', 'request', 'invite', 'email'],
    };

    // Detect query intent
    let queryIntent = null;
    for (const [intent, verbs] of Object.entries(actionVerbs)) {
      if (verbs.some(v => queryLower.includes(v))) {
        queryIntent = intent;
        break;
      }
    }

    // Detect entry intent from question
    let entryIntent = null;
    for (const [intent, verbs] of Object.entries(actionVerbs)) {
      if (verbs.some(v => questionLower.includes(v))) {
        entryIntent = intent;
        break;
      }
    }

    // Matching intent boosts score; mismatched intent penalizes
    if (queryIntent && entryIntent) {
      if (queryIntent === entryIntent) score += 3;
      else score -= 2; // intent mismatch — "how do I create" shouldn't match "what is"
    }

    // ── Signal 4: Query focus alignment (handles "join and then leave" type queries) ──
    // If we detected a primary focus, heavily boost entries whose keywords match that focus
    // and penalize entries that match the non-focus actions.
    if (queryFocus) {
      const entryKeywordsJoined = entry.keywords.join(' ').toLowerCase();
      const focusInEntry = entryKeywordsJoined.includes(queryFocus) || questionLower.includes(queryFocus);
      if (focusInEntry) {
        score += 10; // strong boost — this entry matches the primary focus
      } else {
        // Check if this entry matches a NON-focus action that also appears in the query
        // e.g., query has "join" and "leave" — if focus is "leave" and this entry is about "join", penalize
        const nonFocusMatch = ['leave', 'join', 'create', 'change', 'connect', 'send'].some(action =>
          action !== queryFocus && queryLower.includes(action) && entryKeywordsJoined.includes(action)
        );
        if (nonFocusMatch) score -= 5; // this entry matches the wrong action in the query
      }
    }

    return { entry, score };
  });

  // Only return results above the confidence threshold
  const results = scored
    .filter(s => s.score >= minScore)
    .sort((a, b) => b.score - a.score)
    .slice(0, maxResults)
    .map(s => s.entry);

  // If the top result is barely above threshold and there's a close second,
  // it's ambiguous — return empty to fall through to AI
  if (results.length >= 2) {
    const [first, second] = scored.sort((a, b) => b.score - a.score);
    if (first.score - second.score < 2) {
      // Too close — ambiguous. Let AI handle it.
      return [];
    }
  }

  return results;
}

export default KNOWLEDGE_BASE;
